import { FC } from 'react'
import { FormCheckboxField, FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { PlatformDownloadLinkDto } from 'api/Trading-Accounts/PlatformLink.Dto'
import { PlatformTypeDto } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { NonNegativeIntegerPattern } from 'utils/formValidation'

export interface PlatformDownloadLinkFormValues {
  productLink: string
  enabled: boolean
  order: number | undefined
  platformTypeId: number | undefined
  operatingSystemId: number | undefined
  tickmillCompanyId: number | undefined
}

export interface OuterProps {
  isOpen?: boolean
  onClose(): void
  tickmillCompanies: TickmillCompanyDto[]

  platformTypes: PlatformTypeDto[]
  operatingSystems: NameDto[]

  data?: PlatformDownloadLinkDto | undefined

  onSubmit(values: PlatformDownloadLinkFormValues): Promise<void>
}

type PlatformDownloadLinkProps = OuterProps & FormikProps<PlatformDownloadLinkFormValues>

const PlatformDownloadLinkFormUI: FC<PlatformDownloadLinkProps> = (props) => {
  const { errors, setFieldValue, handleSubmit, isSubmitting, isValid, data, onClose } = props

  const handleOrderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (NonNegativeIntegerPattern.test(event.target.value)) {
      setFieldValue('order', event.target.value === '' ? undefined : Number(event.target.value))
    }
  }

  const handleSubmitForm = () => {
    props.setSubmitting(true)
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {data ? 'Edit Platform Download Link' : 'Add Platform Download Link'}
          </DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormField name='productLink' label='Product Link' error={errors.productLink} />
            <FormSelectField
              name='operatingSystemId'
              label='Operating System'
              options={props.operatingSystems}
              showSearch
              error={errors.operatingSystemId}
              onChange={(value) =>
                setFieldValue('operatingSystemId', value ? Number(value) : undefined)
              }
            />
            <FormSelectField
              name='platformTypeId'
              label='Platform Type'
              options={props.platformTypes.filter((x) => x.enabled)}
              showSearch
              error={errors.platformTypeId}
              onChange={(value) =>
                setFieldValue('platformTypeId', value ? Number(value) : undefined)
              }
            />
            <FormSelectField
              name='tickmillCompanyId'
              label='Tickmill Company'
              options={props.tickmillCompanies}
              showSearch
              error={errors.tickmillCompanyId}
              onChange={(value) =>
                setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)
              }
            />
            <FormField
              name='order'
              label='Order'
              type='number'
              error={errors.order}
              onChange={handleOrderChange}
            />
            <FormCheckboxField name='enabled' label='Enabled' />
          </div>
        </Form>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button disabled={isSubmitting || !isValid} onClick={handleSubmitForm} type='button'>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
export const PlatformDownloadLinkForm = withFormik<OuterProps, PlatformDownloadLinkFormValues>({
  mapPropsToValues: (props) => {
    return {
      productLink: props.data?.productLink || '',
      order: props.data?.order,
      operatingSystemId: props.data?.operatingSystem?.id,
      enabled: props.data?.enabled || false,
      platformTypeId: props.data?.platformType?.id,
      tickmillCompanyId: props.data?.tickmillCompany?.id,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<PlatformDownloadLinkFormValues> = {}

    if (!values.productLink) {
      errors.productLink = 'Required'
    }
    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Required'
    }
    if (!values.platformTypeId) {
      errors.platformTypeId = 'Required'
    }
    if (!values.operatingSystemId) {
      errors.operatingSystemId = 'Required'
    }

    return errors
  },
  isInitialValid: false,
  enableReinitialize: true,
})(PlatformDownloadLinkFormUI)
